import { AriaRole, ForwardedRef, forwardRef, useCallback } from 'react';
import isPropValid from '@emotion/is-prop-valid';
import css, { get } from '@styled-system/css';
import styled from 'styled-components';
import {
  background,
  border,
  color,
  compose,
  flexbox,
  layout,
  position,
  space,
} from 'styled-system';

import { textAlign } from '../../styles';

import { BoxCommonProps, PolymorphicBoxPropsHelperWeb } from './Box.common';

export type Props = BoxCommonProps & {
  /** Forbidden: use `sx` prop instead for styling */
  style?: never;
  /** ARIA role attribute for accessibility https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles */
  role?: AriaRole;
  /** @deprecated Prefer using props.onPress with its hybrid event args instead */
  onClick?: React.MouseEventHandler<HTMLElement>;
  /** DO NOT USE, This powers a legacy usage in the web Button impl */
  __css?: any;
};

const PROPS_TO_FILTER_FROM_DOM_ON_WEB = [
  'display',
  'width',
  'height',
  'size',
  'p',
  'px',
  'py',
  'pt',
  'pr',
  'pb',
  'pl',
  'm',
  'mx',
  'my',
  'mt',
  'mr',
  'mb',
  'ml',
  'tx',
];

const StyledBox = styled.div.withConfig({
  shouldForwardProp: (prop, element) => {
    // string elements are DOM tags going into the HTML, we need to filter props on these
    if (typeof element === 'string') {
      if (PROPS_TO_FILTER_FROM_DOM_ON_WEB.includes(prop)) return false;
      return isPropValid(prop);
    }
    // non-string elements are React components, and we need to pass all props to these
    return true;
  },
})<Props>(
  // NOTE: The order of styles/functions here matters!
  //       Early styles can be overridden by later styles!

  // this is the "style reset" that came with the original reflexbox impl
  {
    boxSizing: 'border-box',
    margin: 0,
    minWidth: 0,
  },

  // for "base" styles via the __css prop, ONLY USED BY THE WEB BUTTON YOU SHOULD AVOID THIS
  (props) => css(props.__css)(props.theme), // eslint-disable-line no-underscore-dangle

  // support the "variant" and "tx" props
  // powers the card and elevation variants of Box, and all the Button variants
  // Not a _great_ pattern but we are stuck with it for now
  ({ theme, variant = '', tx = 'variants' }) =>
    css(get(theme, `${tx}.${variant}`, get(theme, variant)))(theme),

  // "compose" the styled-systems
  compose(space, layout, flexbox, position, color, border, background, textAlign),

  // support responsive props and spacing theme values for the "gap" prop
  (props) => css({ gap: props.gap })(props.theme),

  // support the "sx" prop.  This comes last so it will override any other styles
  (props) => css(props.sx)(props.theme)
);

/**
 * Box is a foundational layout component that provides a flexible container for other elements.
 *
 * READ THE DOCS: https://roverdotcom.atlassian.net/wiki/spaces/TECH/pages/5209129602/Kibble+Core+Box
 */
const Box = <C extends React.ElementType = 'div'>(
  {
    tx = 'variants',
    onPress,
    onClick,
    webProps = {},
    webAs,

    // pull out rxn-native-specific props and do nothing with them
    rxnAs, // eslint-disable-line @typescript-eslint/no-unused-vars
    rxnAsProps, // eslint-disable-line @typescript-eslint/no-unused-vars
    rxnProps, // eslint-disable-line @typescript-eslint/no-unused-vars

    ...other
  }: PolymorphicBoxPropsHelperWeb<Props, C>,
  ref
): JSX.Element => {
  const handlePress = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // onPress gets priority over onClick
      if (onPress) {
        onPress({ webEvent: event });
      } else if (onClick) {
        onClick(event);
      }
    },
    [onPress, onClick]
  );

  const finalProps = {
    ...other,
    ...webProps,
    // don't pass handlePress unless props.onPress or props.onClick is defined
    onClick: onPress || onClick ? handlePress : undefined,
    tx,
    ref,
    as: webAs,
  };

  return <StyledBox {...finalProps} />;
};

export default forwardRef(Box) as <C extends React.ElementType = 'div'>(
  props: PolymorphicBoxPropsHelperWeb<Props, C> & {
    /** The ref type for Box is tricky.  Most of the time it is an HTMLElement,
     * but refining it more narrowly to something like HTMLButtonElement runs into problems.
     * Also, if you do as={FunctionComponent}, there won't be a ref */
    ref?: C extends keyof JSX.IntrinsicElements ? ForwardedRef<HTMLElement> : never;
  }
) => JSX.Element;
